import React, { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import RichTextEditor from "../common/RichTextEditor";
import ConfirmationModal from "../common/ConfirmationModal";
import { useConfirmation } from "../../hooks/useConfirmation";
import { FiEye, FiLock, FiSave, FiTrash2, FiChevronLeft } from "react-icons/fi";

// Helper Functions
function formatDateDDMMYYYY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!d) return iso;
  return `${d}-${m}-${y}`;
}

function formatDateTimeDDMMYYYY(dtStr) {
  if (!dtStr) return "";
  const d = new Date(dtStr);
  const pad = n => n.toString().padStart(2, "0");
  return (
    pad(d.getDate()) +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    d.getFullYear() +
    " " +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

// Sub-components
function AuditBanner({ note }) {
  if (!note || !note.finalised) return null;
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 mb-6">
      <FiLock className="w-5 h-5 text-green-600" />
      <div>
        <div className="font-semibold text-green-800">Finalised</div>
        <div className="text-sm text-green-600">
          by {note.finalisedBy} on {formatDateTimeDDMMYYYY(note.finalisedAt)}
        </div>
      </div>
    </div>
  );
}

function ReadOnlyNotePanel({ note }) {
  if (!note)
    return (
      <div className="text-center py-8">
        <div className="text-lg font-semibold text-gray-500 mb-2">No previous SOAP note</div>
        <div className="text-gray-400">This client does not have a previous note.</div>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Previous SOAP Note</h3>
        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
          {formatDateDDMMYYYY(note.date)} {note.time}
        </span>
      </div>
      <AuditBanner note={note} />
      {["subjective", "objective", "assessment", "plan"].map(field => (
        <div className="bg-gray-50 rounded-lg p-4" key={field}>
          <div className="font-semibold text-gray-700 mb-2">
            {field.charAt(0).toUpperCase() + field.slice(1)}:
          </div>
          <div
            className="text-gray-600 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{
              __html: note[field] || "<span class='text-gray-400 italic'>None</span>"
            }}
          />
        </div>
      ))}
      {note.addenda && note.addenda.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="font-semibold text-gray-700 mb-2">Addenda:</div>
          <div className="space-y-2">
            {[...note.addenda]
              .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
              .map((a, idx) => (
                <div key={idx} className="p-3 bg-white rounded border-l-4 border-purple-400">
                  <div className="text-gray-800 mb-1">{a.text}</div>
                  <div className="text-xs text-gray-500">
                    — {a.author}, {formatDateTimeDDMMYYYY(a.date)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SoapNoteModal({
  appointment,
  client,
  onClose,
  onNoteSaved,
  onNoteDeleted
}) {
  const { user } = useUser();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel: handleConfirmationCancel } = useConfirmation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [existing, setExisting] = useState(null);

  // Load notes from Firebase
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const { getNotes } = await import('../../services/noteService');
        const allNotes = await getNotes();
        setNotes(allNotes);
        const existingNote = allNotes.find(n => n.appointmentId === appointment.id);
        setExisting(existingNote);
        
        if (existingNote) {
          setSubjective(existingNote.subjective || "");
          setObjective(existingNote.objective || "");
          setAssessment(existingNote.assessment || "");
          setPlan(existingNote.plan || "");
          setAddenda(existingNote.addenda || []);
          setFinalised(existingNote.finalised || false);
          setFinalisedBy(existingNote.finalisedBy || "");
          setFinalisedAt(existingNote.finalisedAt || "");
        }
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadNotes();
  }, [appointment.id]);

  const [subjective, setSubjective] = useState(existing?.subjective || "");
  const [objective, setObjective] = useState(existing?.objective || "");
  const [assessment, setAssessment] = useState(existing?.assessment || "");
  const [plan, setPlan] = useState(existing?.plan || "");
  const [addenda, setAddenda] = useState(existing?.addenda || []);
  const [finalised, setFinalised] = useState(existing?.finalised || false);
  const [finalisedBy, setFinalisedBy] = useState(existing?.finalisedBy || "");
  const [finalisedAt, setFinalisedAt] = useState(existing?.finalisedAt || "");
  const [showAddendum, setShowAddendum] = useState(false);
  const [addendumText, setAddendumText] = useState("");
  const [showSideBySide, setShowSideBySide] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(
      subjective !== (existing?.subjective || "") ||
      objective !== (existing?.objective || "") ||
      assessment !== (existing?.assessment || "") ||
      plan !== (existing?.plan || "")
    );
    // eslint-disable-next-line
  }, [subjective, objective, assessment, plan]);

  const canEdit =
    user.role === "admin" ||
    (user.role === "clinician" && user.userId === appointment.staffId);

  async function saveNote({ asFinal = false } = {}) {
    if (!canEdit) return;
    const now = new Date();
    const nowString = now.toISOString();
    const newNote = {
      id: existing?.id || `${appointment.id}-note`,
      appointmentId: appointment.id,
      clientId: client.id,
      date: appointment.date,
      time: appointment.time,
      clinicianId: appointment.staffId,
      subjective,
      objective,
      assessment,
      plan,
      addenda,
      finalised: asFinal ? true : false,
      finalisedBy: asFinal ? user.name : finalisedBy,
      finalisedAt: asFinal ? nowString : finalisedAt,
    };

    try {
      const { saveNote: saveNoteService } = await import('../../services/noteService');
      const savedNote = await saveNoteService(newNote);
      
      const updatedNotes = existing
        ? notes.map(n => (n.id === savedNote.id ? savedNote : n))
        : [...notes, savedNote];
      setNotes(updatedNotes);
      setExisting(savedNote);
      
      // Update appointment status via appointment service
      const { appointmentService } = await import('../../services/appointmentService');
      await appointmentService.updateAppointment(appointment.id, {
        noteId: savedNote.id,
        noteStatus: asFinal ? "green" : "amber"
      });
    } catch (error) {
      console.error('Error saving note:', error);
      return;
    }

    setFinalised(asFinal ? true : false);
    setFinalisedBy(asFinal ? user.name : finalisedBy);
    setFinalisedAt(asFinal ? nowString : finalisedAt);
    setDirty(false);
    if (onNoteSaved) onNoteSaved(newNote);
    if (onClose) onClose();
  }

  async function handleCancel() {
    if (dirty) {
      const confirmed = await showConfirmation({
        title: "Unsaved Changes",
        message: "You have unsaved changes. Discard them?",
        confirmText: "Discard",
        cancelText: "Keep Editing",
        type: "warning"
      });
      if (!confirmed) return;
    }
    if (onClose) onClose();
  }

  async function handleFinalise() {
    const confirmed = await showConfirmation({
      title: "Finalise SOAP Note",
      message: "Once finalised, this note cannot be changed. Are you sure you want to finalise it?",
      confirmText: "Finalise",
      cancelText: "Cancel",
      type: "warning"
    });
    
    if (confirmed) {
      saveNote({ asFinal: true });
    }
  }

  async function handleAddAddendum() {
    if (!addendumText.trim()) return;

    const addendum = {
      id: Date.now().toString(),
      text: addendumText.trim(),
      addedBy: user.name || user.email,
      addedAt: new Date().toISOString()
    };

    const updatedAddenda = [...addenda, addendum];
    setAddenda(updatedAddenda);

    const updatedNote = {
      ...existing,
      addenda: updatedAddenda,
      updatedAt: new Date().toISOString()
    };

    try {
      const { saveNote: saveNoteService } = await import('../../services/noteService');
      const savedNote = await saveNoteService(updatedNote);
      
      const updatedNotes = notes.map(n =>
        n.id === savedNote.id ? savedNote : n
      );
      setNotes(updatedNotes);
      setExisting(savedNote);
    } catch (error) {
      console.error('Error adding addendum:', error);
    }
    
    setAddendumText("");
    setShowAddendum(false);
  }

  async function handleDelete() {
    const confirmed = await showConfirmation({
      title: "Delete SOAP Note",
      message: "Delete this SOAP note? This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (!confirmed) return;

    try {
      const { deleteNote } = await import('../../services/noteService');
      await deleteNote(existing.id);
      
      const updatedNotes = notes.filter(n => n.appointmentId !== appointment.id);
      setNotes(updatedNotes);
      setExisting(null);
      
      // Update appointment status via appointment service
      const { appointmentService } = await import('../../services/appointmentService');
      await appointmentService.updateAppointment(appointment.id, {
        noteId: undefined,
        noteStatus: "red"
      });
      
      if (onNoteDeleted) {
        onNoteDeleted();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
    
    onClose();
  }

  const isReadOnly = finalised || !canEdit;

  const lastNote = notes
    .filter(n => n.clientId === client.id && n.appointmentId !== appointment.id)
    .sort((a, b) => {
      const da = new Date(a.date + "T" + (a.time || "00:00"));
      const db = new Date(b.date + "T" + (b.time || "00:00"));
      return db - da;
    })[0];

  const soapSections = [
    { label: "Subjective", value: subjective, setter: setSubjective },
    { label: "Objective", value: objective, setter: setObjective },
    { label: "Assessment", value: assessment, setter: setAssessment },
    { label: "Plan", value: plan, setter: setPlan }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-xl shadow-2xl w-full max-h-[95vh] overflow-y-auto transition-all duration-200 ${showSideBySide ? 'max-w-7xl' : 'max-w-4xl'
        }`}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FiChevronLeft className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900">
                  SOAP Note for {client.name}
                </h2>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  {formatDateDDMMYYYY(appointment.date)}
                </span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                  {appointment.time}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSideBySide(!showSideBySide)}
              disabled={!lastNote}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${lastNote
                  ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              title={
                showSideBySide
                  ? "Hide previous SOAP note"
                  : lastNote
                    ? "Show previous SOAP note side by side"
                    : "No previous SOAP notes"
              }
            >
              <FiEye className="w-4 h-4" />
              {showSideBySide ? "Hide Last Note" : "View Last Note"}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 ${showSideBySide ? 'grid grid-cols-2 gap-8' : 'flex flex-col gap-6'
          }`}>
          <div className="flex flex-col gap-6">
            {/* Finalised Banner */}
            <AuditBanner note={{ finalised, finalisedBy, finalisedAt }} />

            {/* SOAP Sections */}
            {soapSections.map((section, idx) => (
              <div key={section.label} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">{section.label}</h3>
                </div>
                <div className="min-h-[120px]">
                  <RichTextEditor
                    value={section.value}
                    onChange={section.setter}
                    readOnly={isReadOnly}
                    placeholder={`Enter ${section.label.toLowerCase()}...`}
                    minHeight={120}
                  />
                </div>
              </div>
            ))}

            {/* Addenda Section */}
            {finalised && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-purple-500 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">Addenda</h3>
                </div>

                {addenda && addenda.length > 0 && (
                  <div className="mb-4">
                    {[...addenda]
                      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
                      .map((a, idx) => (
                        <div key={idx} className="mb-3 p-3 bg-gray-50 rounded-lg border-l-4 border-purple-400">
                          <div className="text-gray-800 mb-1">{a.text}</div>
                          <div className="text-xs text-gray-500">
                            — {a.author}, {formatDateTimeDDMMYYYY(a.date)}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {canEdit && (
                  <div>
                    {showAddendum ? (
                      <div className="space-y-3">
                        <RichTextEditor
                          value={addendumText}
                          onChange={setAddendumText}
                          minHeight={60}
                          placeholder="Enter addendum..."
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            onClick={handleAddAddendum}
                          >
                            Save Addendum
                          </button>
                          <button
                            type="button"
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            onClick={() => setShowAddendum(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                        onClick={() => setShowAddendum(true)}
                      >
                        + Add Addendum
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                onClick={handleDelete}
              >
                <FiTrash2 className="w-4 h-4" />
                Delete
              </button>
              {!finalised && canEdit && (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                    onClick={e => { e.preventDefault(); saveNote(); }}
                  >
                    <FiSave className="w-4 h-4" />
                    Save
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                    onClick={handleFinalise}
                  >
                    <FiLock className="w-4 h-4" />
                    Finalise
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Side-by-side Previous Note */}
          {showSideBySide && (
            <div className="bg-gray-50 rounded-lg p-6">
              <ReadOnlyNotePanel note={lastNote} />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleConfirmationCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </div>
  );
}
